import { useCallback, useEffect, useRef, useState } from 'react';
import { Cloud, Copy, HardDrive, KeyRound, Loader2, Play, Plus, Power, RefreshCw, RotateCw, Server, Trash2, Wifi } from 'lucide-react';
import { api, ApiError } from '../api/client.js';

/**
 * /cloud — Cloud Servers (DigitalOcean + Linode + Scaleway at the provider level).
 *
 * Phase A: provider accounts manager — paste API token → test → save
 * (encrypted server-side). Servers list/create/power arrive in Phase B+.
 */
export function CloudServers() {
  const [accounts, setAccounts] = useState(null);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const load = useCallback(async () => {
    try {
      const data = await api('/api/cloud/accounts');
      setAccounts(data.accounts);
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'network_error');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-t3">
            Manage DigitalOcean droplets, Linodes & Scaleway Instances at the provider level.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowForm((v) => !v)}
          className="inline-flex items-center gap-1.5 bg-blue text-white text-sm px-3 py-1.5 rounded-md hover:bg-blue-deep"
        >
          <Plus className="w-4 h-4" /> Add account
        </button>
      </div>

      {showForm && (
        <AddAccountForm onCreated={() => { setShowForm(false); load(); }} />
      )}

      {error && (
        <div className="bg-err-bg border border-err-bd text-err text-sm rounded-md px-4 py-3">
          {error}
        </div>
      )}

      {accounts === null ? (
        <p className="text-sm text-t3">Loading…</p>
      ) : accounts.length === 0 ? (
        <div className="bg-white border border-bd rounded-lg p-8 text-center text-sm text-t3">
          <Cloud className="w-8 h-8 mx-auto mb-3 text-ic-light" />
          No provider accounts yet. Add a DigitalOcean, Linode or Scaleway API key
          to list and manage your cloud servers from the panel.
        </div>
      ) : (
        <AccountsTable accounts={accounts} onChanged={load} />
      )}
    </div>
  );
}

const PROVIDER_META = {
  do: { name: 'DigitalOcean', cls: 'bg-s-blue text-blue border-bd-blue' },
  linode: { name: 'Linode', cls: 'bg-s-green text-ok border-bd' },
  scw: { name: 'Scaleway', cls: 'bg-s-3 text-t1 border-bd-strong' },
};

function ProviderBadge({ provider }) {
  const meta = PROVIDER_META[provider] ?? { name: provider, cls: 'bg-s-3 text-t2 border-bd' };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium border ${meta.cls}`}>
      {meta.name}
    </span>
  );
}

function StatusBadge({ status }) {
  const styles = {
    ok: 'bg-s-green text-ok border-bd',
    failed: 'bg-err-bg text-err border-err-bd',
    new: 'bg-s-3 text-t2 border-bd',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium border ${styles[status] ?? styles.new}`}>
      {status}
    </span>
  );
}

// ─── Accounts table ─────────────────────────────────────────────────────────

function AccountsTable({ accounts, onChanged }) {
  return (
    <div className="bg-white border border-bd rounded-lg overflow-hidden">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs uppercase tracking-wide text-t3 border-b border-bd">
            <th className="px-4 py-3">Provider</th>
            <th className="px-4 py-3">Label</th>
            <th className="px-4 py-3">Status</th>
            <th className="px-4 py-3">Last checked</th>
            <th className="px-4 py-3">Added</th>
            <th className="px-4 py-3 text-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          {accounts.map((a) => (
            <AccountRow key={a.id} account={a} onChanged={onChanged} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AccountRow({ account, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [serversOpen, setServersOpen] = useState(false);

  async function handleTest() {
    setBusy(true);
    setTestResult(null);
    try {
      const r = await api(`/api/cloud/accounts/${account.id}/test`, { method: 'POST' });
      setTestResult({ ok: true, detail: r.email ?? r.detail });
    } catch (err) {
      setTestResult({ ok: false, detail: err.body?.detail ?? err.message });
    } finally {
      setBusy(false);
      onChanged();
    }
  }

  async function handleDelete() {
    const typed = window.prompt(
      `Type the account label to confirm deletion:\n${account.label}\n\n(The token is deleted from the panel — nothing happens on the provider side.)`,
    );
    if (typed === null) return;
    if (typed !== account.label) {
      window.alert('Label did not match — not deleted.');
      return;
    }
    setBusy(true);
    try {
      await api(`/api/cloud/accounts/${account.id}`, {
        method: 'DELETE',
        body: { confirm_label: typed },
      });
      onChanged();
    } catch (err) {
      window.alert(`Delete failed: ${err.message}`);
      setBusy(false);
    }
  }

  return (
    <>
    <tr className="border-b border-bd last:border-0">
      <td className="px-4 py-3"><ProviderBadge provider={account.provider} /></td>
      <td className="px-4 py-3 font-medium text-t1">{account.label}</td>
      <td className="px-4 py-3">
        <StatusBadge status={account.status} />
        {testResult && (
          <span className={`ml-2 text-xs ${testResult.ok ? 'text-ok' : 'text-err'}`}>
            {testResult.detail}
          </span>
        )}
      </td>
      <td className="px-4 py-3 text-t3 text-xs">
        {account.last_checked_at ? new Date(`${account.last_checked_at}Z`).toLocaleString() : '—'}
      </td>
      <td className="px-4 py-3 text-t3 text-xs">
        {new Date(`${account.created_at}Z`).toLocaleDateString()}
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center justify-end gap-1.5">
          <button
            type="button"
            onClick={() => setServersOpen((v) => !v)}
            className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md border border-bd-blue text-blue hover:bg-s-blue"
          >
            <Server className="w-3.5 h-3.5" /> Servers
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={handleTest}
            className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md border border-bd text-t2 hover:bg-s-2 disabled:opacity-50"
          >
            {busy ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Wifi className="w-3.5 h-3.5" />}
            Test
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={handleDelete}
            className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md border border-err-bd text-err hover:bg-err-bg disabled:opacity-50"
          >
            <Trash2 className="w-3.5 h-3.5" /> Delete
          </button>
        </div>
      </td>
    </tr>
    {serversOpen && (
      <tr className="border-b border-bd last:border-0 bg-s-blue/30">
        <td colSpan={6} className="px-4 py-4">
          <ServersPanel account={account} />
        </td>
      </tr>
    )}
    </>
  );
}

// ─── Add account form (paste token → test → save) ───────────────────────────

function AddAccountForm({ onCreated }) {
  const [provider, setProvider] = useState('do');
  const [label, setLabel] = useState('');
  const [token, setToken] = useState('');
  const [projectId, setProjectId] = useState('');   // Scaleway only
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testResult, setTestResult] = useState(null);   // { ok, email?, detail }
  const [error, setError] = useState(null);

  const isScw = provider === 'scw';
  const canTest = token.trim().length >= 8 && (!isScw || projectId.trim()) && !testing && !saving;
  // Scaleway's account = secret key + project id; the backend stores both encrypted as one secret.
  const credBody = { provider, token: token.trim(), ...(isScw ? { project_id: projectId.trim() } : {}) };
  const canSave = testResult?.ok && label.trim().length >= 2 && !testing && !saving;

  // Any change to provider/token invalidates a previous test result.
  function onProviderChange(v) { setProvider(v); setTestResult(null); setError(null); }
  function onTokenChange(v) { setToken(v); setTestResult(null); setError(null); }
  function onProjectChange(v) { setProjectId(v); setTestResult(null); setError(null); }

  async function handleTest() {
    setTesting(true);
    setError(null);
    setTestResult(null);
    try {
      const r = await api('/api/cloud/accounts/test', {
        method: 'POST',
        body: credBody,
      });
      setTestResult({ ok: true, email: r.email, detail: r.detail });
    } catch (err) {
      setTestResult({ ok: false, detail: err.body?.detail?.join?.(', ') ?? err.body?.detail ?? err.message });
    } finally {
      setTesting(false);
    }
  }

  async function handleSave(e) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await api('/api/cloud/accounts', {
        method: 'POST',
        body: { ...credBody, label: label.trim() },
      });
      onCreated();
    } catch (err) {
      setError(err.body?.detail?.join?.(', ') ?? err.body?.detail ?? err.message);
      setSaving(false);
    }
  }

  return (
    <form
      onSubmit={handleSave}
      className="bg-white border border-bd rounded-lg p-5 space-y-4"
    >
      <div className="flex items-center gap-2 text-sm font-semibold text-t1">
        <KeyRound className="w-4 h-4 text-t3" />
        Add provider account
      </div>

      <div className="grid sm:grid-cols-3 gap-4">
        <label className="block">
          <span className="text-xs font-medium text-t2">Provider</span>
          <select
            value={provider}
            onChange={(e) => onProviderChange(e.target.value)}
            className="mt-1 w-full rounded-md border border-bd-strong px-2.5 py-1.5 text-sm bg-white"
          >
            <option value="do">DigitalOcean</option>
            <option value="linode">Linode</option>
            <option value="scw">Scaleway</option>
          </select>
        </label>

        <label className="block">
          <span className="text-xs font-medium text-t2">Label</span>
          <input
            type="text"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder={provider === 'do' ? 'DO main' : provider === 'scw' ? 'Scaleway main' : 'Linode main'}
            className="mt-1 w-full rounded-md border border-bd-strong px-2.5 py-1.5 text-sm"
          />
        </label>

        <label className="block">
          <span className="text-xs font-medium text-t2">{isScw ? 'Secret key' : 'API token'}</span>
          <input
            type="password"
            value={token}
            onChange={(e) => onTokenChange(e.target.value)}
            placeholder={isScw ? 'API key secret key — stored encrypted' : 'paste token — stored encrypted'}
            autoComplete="off"
            className="mt-1 w-full rounded-md border border-bd-strong px-2.5 py-1.5 text-sm font-mono"
          />
        </label>

        {isScw && (
          <label className="block sm:col-span-3">
            <span className="text-xs font-medium text-t2">Project ID</span>
            <input
              type="text"
              value={projectId}
              onChange={(e) => onProjectChange(e.target.value)}
              placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
              autoComplete="off"
              className="mt-1 w-full rounded-md border border-bd-strong px-2.5 py-1.5 text-sm font-mono"
            />
            <span className="text-[11px] text-t3">
              Scaleway console → IAM → API keys → Generate, for a user or application with the Instances
              permission on this project (plus Block Storage, so Delete can remove the server's volumes).
              Paste the <em>secret</em> key, not the access key. The project ID is under Project → Settings.
            </span>
          </label>
        )}
      </div>

      {testResult && (
        <div
          className={`text-sm rounded-md px-3 py-2 border ${
            testResult.ok
              ? 'bg-s-green border-bd text-ok'
              : 'bg-err-bg border-err-bd text-err'
          }`}
        >
          {testResult.ok
            ? <>Token OK{testResult.email ? <> — account <span className="font-medium">{testResult.email}</span></> : null} ({testResult.detail})</>
            : <>Test failed: {testResult.detail}</>}
        </div>
      )}
      {error && (
        <div className="text-sm rounded-md px-3 py-2 border bg-err-bg border-err-bd text-err">
          {error}
        </div>
      )}

      <div className="flex items-center gap-2">
        <button
          type="button"
          disabled={!canTest}
          onClick={handleTest}
          className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md border border-bd-strong text-t2 hover:bg-s-2 disabled:opacity-50"
        >
          {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wifi className="w-4 h-4" />}
          Test connection
        </button>
        <button
          type="submit"
          disabled={!canSave}
          title={canSave ? undefined : 'Run a successful connection test first'}
          className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md bg-blue text-white hover:bg-blue-deep disabled:opacity-50"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
          Save account
        </button>
        <span className="text-xs text-t3 ml-1">
          The token is encrypted with the panel master key and never shown again.
        </span>
      </div>
    </form>
  );
}

// ─── Servers panel (Phase B: list / create / delete) ────────────────────────

function ServerStatusBadge({ status, providerStatus }) {
  const styles = {
    active: 'bg-s-green text-ok border-bd',
    off: 'bg-s-3 text-t2 border-bd',
    provisioning: 'bg-warn-bg text-[#8B5206] border-warn-bd animate-pulse',
    other: 'bg-s-3 text-t3 border-bd',
  };
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium border ${styles[status] ?? styles.other}`}
      title={providerStatus}
    >
      {status === 'other' ? providerStatus : status}
    </span>
  );
}

function ServersPanel({ account }) {
  const [servers, setServers] = useState(null);
  const [truncated, setTruncated] = useState(false);
  const [zoneErrors, setZoneErrors] = useState([]);  // Scaleway: zones that could not be read
  const [error, setError] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [rootPasses, setRootPasses] = useState([]); // [{ name, pass }] — one-time reveal
  const [refreshing, setRefreshing] = useState(false);
  const [watching, setWatching] = useState(0);      // forced poll ticks after a power action
  const [selected, setSelected] = useState(() => new Set()); // server ids picked for bulk delete

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      const data = await api(`/api/cloud/accounts/${account.id}/servers`);
      setServers(data.servers);
      setTruncated(!!data.truncated);
      setZoneErrors(data.zone_errors ?? []);
      setError(null);
    } catch (err) {
      setError(err.body?.detail ?? err.message);
    } finally {
      setRefreshing(false);
    }
  }, [account.id]);

  useEffect(() => { load(); }, [load]);

  // Poll the list every 5s (bounded) while anything is provisioning, or for a
  // couple of minutes after a power action — statuses settle asynchronously
  // at the provider, so the operator watches them flip without clicking.
  const pollsLeft = useRef(60);
  useEffect(() => {
    const shouldPoll = servers?.some((s) => s.status === 'provisioning') || watching > 0;
    if (!shouldPoll) { pollsLeft.current = 60; return; }
    if (pollsLeft.current <= 0) return;
    const t = setTimeout(() => {
      pollsLeft.current -= 1;
      setWatching((w) => (w > 0 ? w - 1 : 0));
      load();
    }, 5000);
    return () => clearTimeout(t);
  }, [servers, watching, load]);

  async function handlePower(server, action) {
    if (action === 'stop') {
      const how = account.provider === 'do'
        ? 'Hard power-off (like pulling the plug).\nBilling continues while a server is off.'
        : account.provider === 'scw'
          ? 'Full power-off (releases the hypervisor slot).\nCompute billing stops; volumes and flexible IPs keep billing.\nA dynamic public IP can change when it starts again.'
          : 'Graceful shutdown (ACPI).\nBilling continues while a server is off.';
      if (!window.confirm(`Power OFF "${server.name}"?\n\n${how}`)) return;
    }
    if (action === 'reboot' && !window.confirm(`Reboot "${server.name}"?\nThe server will be briefly unreachable.`)) return;
    try {
      await api(`/api/cloud/accounts/${account.id}/servers/${server.id}/power`, {
        method: 'POST',
        body: { action },
      });
      setWatching(24); // ~2 min of 5s polls
      load();
    } catch (err) {
      window.alert(`${action} failed: ${err.body?.detail ?? err.message}`);
    }
  }

  async function handleDelete(server) {
    const typed = window.prompt(
      `DELETE server "${server.name}" (${server.ipv4_all?.length > 1 ? server.ipv4_all.map((ip) => ip.address).join(', ') : (server.ipv4_public ?? 'no IP')})?\n\nThis destroys the machine and its disks${account.provider === 'scw' ? `, volumes and ${server.ipv4_all?.length > 1 ? `all ${server.ipv4_all.length} ` : ''}public IPs` : ''} at the provider. Type the server name to confirm:`,
    );
    if (typed === null) return;
    try {
      await api(`/api/cloud/accounts/${account.id}/servers/${server.id}`, {
        method: 'DELETE',
        body: { confirm_name: typed },
      });
      load();
    } catch (err) {
      window.alert(
        err.body?.error === 'confirm_name_mismatch'
          ? 'Name did not match — not deleted.'
          : `Delete failed: ${err.body?.detail ?? err.message}`,
      );
      if (err.body?.error === 'partial_delete') load();
    }
  }

  // Scaleway: the answer carries the server as re-read after the change —
  // patch that row in, then reload the list.
  function patchServer(server) {
    if (server) setServers((prev) => prev?.map((s) => (s.id === server.id ? server : s)) ?? prev);
  }

  async function handleAddIp(server) {
    const n = server.ipv4_all?.length ?? 1;
    if (!window.confirm(`Add a public IPv4 to "${server.name}" (${n} → ${n + 1})?\n\nIt bills like the others until removed. Scaleway's images configure it in the OS by themselves.`)) return;
    try {
      const r = await api(`/api/cloud/accounts/${account.id}/servers/${server.id}/ips`, { method: 'POST' });
      patchServer(r.server);
      window.alert(`Added ${r.ip.address} — the server now lists ${r.server.ipv4_all.length} IPv4.`);
    } catch (err) {
      patchServer(err.body?.server);
      window.alert(`Add IP failed: ${err.body?.detail ?? err.message}`);
    }
    load();
  }

  async function handleRemoveIp(server, ip) {
    const typed = window.prompt(
      `REMOVE ${ip.address} from "${server.name}"?\n\nThe IP is released back to Scaleway — you will not get the same address again. Type the IP to confirm:`,
    );
    if (typed === null) return;
    try {
      const r = await api(`/api/cloud/accounts/${account.id}/servers/${server.id}/ips/${ip.id}`, {
        method: 'DELETE',
        body: { confirm_address: typed.trim() },
      });
      patchServer(r.server);
    } catch (err) {
      patchServer(err.body?.server);
      window.alert(
        err.body?.error === 'confirm_address_mismatch'
          ? 'Address did not match — not removed.'
          : `Remove IP failed: ${err.body?.detail ?? err.message}`,
      );
    }
    load();
  }

  const selectedTargets = (servers ?? []).filter((s) => selected.has(s.id));

  function toggleSelected(id) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  function toggleSelectAll() {
    setSelected((prev) =>
      prev.size === (servers?.length ?? 0) ? new Set() : new Set(servers.map((s) => s.id)),
    );
  }

  async function handleBulkDelete() {
    if (!selectedTargets.length) return;
    const typed = window.prompt(
      `DELETE ${selectedTargets.length} server(s)?\n\n${selectedTargets
        .map((t) => `• ${t.name} (${t.ipv4_public ?? 'no IP'})`)
        .join('\n')}\n\nThis destroys the machines and their disks at the provider.\nType the number of servers (${selectedTargets.length}) to confirm:`,
    );
    if (typed === null) return;
    if (Number(typed) !== selectedTargets.length) {
      window.alert('Count did not match — nothing deleted.');
      return;
    }
    try {
      const resp = await api(`/api/cloud/accounts/${account.id}/servers/bulk-delete`, {
        method: 'POST',
        body: { server_ids: selectedTargets.map((t) => t.id), confirm_count: selectedTargets.length },
      });
      const failed = (resp.results ?? []).filter((r) => !r.ok);
      if (failed.length) {
        window.alert(`Some deletes failed:\n${failed.map((f) => `${f.server_id}: ${f.detail ?? f.error}`).join('\n')}`);
      }
      setSelected(new Set());
      load();
    } catch (err) {
      window.alert(`Bulk delete failed: ${err.body?.detail ?? err.message}`);
    }
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm font-semibold text-t1">
          <HardDrive className="w-4 h-4 text-t3" />
          Servers on {account.label}
        </div>
        <div className="flex items-center gap-1.5">
          {selectedTargets.length > 0 && (
            <button
              type="button"
              onClick={handleBulkDelete}
              className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md border border-err-bd bg-err-bg text-err hover:bg-err-bg"
            >
              <Trash2 className="w-3.5 h-3.5" /> Delete selected ({selectedTargets.length})
            </button>
          )}
          <button
            type="button"
            onClick={load}
            disabled={refreshing}
            className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md border border-bd text-t2 hover:bg-s-2 disabled:opacity-50"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} /> Refresh
          </button>
          <button
            type="button"
            onClick={() => setShowCreate((v) => !v)}
            className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md bg-blue text-white hover:bg-blue-deep"
          >
            <Plus className="w-3.5 h-3.5" /> Create server
          </button>
        </div>
      </div>

      {rootPasses.length > 0 && (
        <div className="bg-warn-bg border border-warn-bd rounded-md px-4 py-3 text-sm text-[#8B5206]">
          <div className="font-semibold mb-1">
            Root password{rootPasses.length > 1 ? 's' : ''} — shown ONCE, copy now.
          </div>
          <div className="space-y-1">
            {rootPasses.map((rp) => (
              <div key={rp.name} className="flex items-center gap-2">
                <span className="text-xs font-medium w-40 truncate">{rp.name}</span>
                <code className="font-mono text-xs bg-white border border-warn-bd rounded px-2 py-1">
                  {rp.pass}
                </code>
                <button
                  type="button"
                  onClick={() => navigator.clipboard?.writeText(rp.pass)}
                  className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md border border-warn-bd text-[#8B5206] hover:bg-warn-bg"
                >
                  <Copy className="w-3.5 h-3.5" /> Copy
                </button>
              </div>
            ))}
          </div>
          <div className="flex items-center gap-3 mt-2">
            {rootPasses.length > 1 && (
              <button
                type="button"
                onClick={() =>
                  navigator.clipboard?.writeText(rootPasses.map((rp) => `${rp.name}: ${rp.pass}`).join('\n'))
                }
                className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md border border-warn-bd text-[#8B5206] hover:bg-warn-bg"
              >
                <Copy className="w-3.5 h-3.5" /> Copy all
              </button>
            )}
            <button
              type="button"
              onClick={() => setRootPasses([])}
              className="text-xs text-warn underline"
            >
              I saved {rootPasses.length > 1 ? 'them' : 'it'} — dismiss
            </button>
          </div>
          <p className="text-xs mt-1.5">
            The panel does not store these passwords. If you picked an SSH key you can also log in with the key.
          </p>
        </div>
      )}

      {showCreate && (
        <CreateServerForm
          account={account}
          onCreated={(resp) => {
            setShowCreate(false);
            const passes = (resp.results ?? [])
              .filter((r) => r.ok && r.root_pass)
              .map((r) => ({ name: r.name, pass: r.root_pass }));
            if (passes.length) setRootPasses(passes);
            const failed = (resp.results ?? []).filter((r) => !r.ok);
            if (failed.length) {
              window.alert(`Some servers failed to create:\n${failed.map((f) => `${f.name}: ${f.detail ?? f.error}`).join('\n')}`);
            }
            // A server with fewer IPs than asked is PARTIAL, said first.
            const warned = (resp.results ?? []).filter((r) => r.ok && r.warning);
            if (warned.length) {
              window.alert(warned.map((w) => `${w.partial ? 'PARTIAL — ' : ''}${w.name}: ${w.warning}`).join('\n'));
            }
            if (resp.ip_leftovers?.length) {
              window.alert(`Reserved IPs that could NOT be released (they bill — delete them in the Scaleway console):\n${resp.ip_leftovers.map((l) => l.id).join('\n')}`);
            }
            load();
          }}
        />
      )}

      {error && (
        <div className="bg-err-bg border border-err-bd text-err text-sm rounded-md px-3 py-2">{error}</div>
      )}

      {servers === null ? (
        <p className="text-sm text-t3">Loading…</p>
      ) : servers.length === 0 ? (
        <p className="text-sm text-t3 bg-white border border-bd rounded-md px-4 py-4 text-center">
          No servers on this account yet.
        </p>
      ) : (
        <div className="bg-white border border-bd rounded-md overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-t3 border-b border-bd">
                <th className="px-3 py-2 w-8">
                  <input
                    type="checkbox"
                    checked={servers.length > 0 && selected.size === servers.length}
                    onChange={toggleSelectAll}
                    title="Select all"
                  />
                </th>
                <th className="px-3 py-2">Name</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Region</th>
                <th className="px-3 py-2">Size</th>
                <th className="px-3 py-2">Public IP</th>
                <th className="px-3 py-2">Created</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {servers.map((s) => (
                <tr key={s.id} className="border-b border-bd last:border-0">
                  <td className="px-3 py-2">
                    <input
                      type="checkbox"
                      checked={selected.has(s.id)}
                      onChange={() => toggleSelected(s.id)}
                    />
                  </td>
                  <td className="px-3 py-2 font-medium text-t1">{s.name}</td>
                  <td className="px-3 py-2">
                    <ServerStatusBadge status={s.status} providerStatus={s.provider_status} />
                  </td>
                  <td className="px-3 py-2 text-t2 text-xs">{s.region ?? '—'}</td>
                  <td className="px-3 py-2 text-t2 text-xs font-mono">{s.size ?? '—'}</td>
                  <td className="px-3 py-2 text-t2 text-xs font-mono">
                    {s.ipv4_all?.length ? (
                      <div className="space-y-0.5">
                        {s.ipv4_all.map((ip) => (
                          <div key={ip.id} className="flex items-center gap-1">
                            <span>{ip.address ?? '…'}</span>
                            {ip.address && (
                              <button
                                type="button"
                                onClick={() => navigator.clipboard?.writeText(ip.address)}
                                title="Copy"
                                className="text-t3 hover:text-t1"
                              >
                                <Copy className="w-3 h-3" />
                              </button>
                            )}
                            {s.ipv4_all.length > 1 && (
                              <button
                                type="button"
                                onClick={() => handleRemoveIp(s, ip)}
                                title="Remove this IP"
                                className="text-t3 hover:text-err"
                              >
                                <Trash2 className="w-3 h-3" />
                              </button>
                            )}
                          </div>
                        ))}
                        {account.provider === 'scw' && s.ipv4_all.length < 5 && s.status !== 'provisioning' && (
                          <button
                            type="button"
                            onClick={() => handleAddIp(s)}
                            className="inline-flex items-center gap-0.5 text-[11px] font-sans text-blue hover:underline"
                          >
                            <Plus className="w-3 h-3" /> Add IP ({s.ipv4_all.length}/5)
                          </button>
                        )}
                      </div>
                    ) : (s.ipv4_public ?? '…')}
                  </td>
                  <td className="px-3 py-2 text-t3 text-xs">
                    {s.created_at ? new Date(s.created_at).toLocaleDateString() : '—'}
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex items-center justify-end gap-1.5">
                      {s.status === 'off' && (
                        <button
                          type="button"
                          onClick={() => handlePower(s, 'start')}
                          className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md border border-bd text-ok hover:bg-s-green"
                        >
                          <Play className="w-3.5 h-3.5" /> Start
                        </button>
                      )}
                      {s.status === 'active' && (
                        <>
                          <button
                            type="button"
                            onClick={() => handlePower(s, 'stop')}
                            className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md border border-warn-bd text-warn hover:bg-warn-bg"
                          >
                            <Power className="w-3.5 h-3.5" /> Stop
                          </button>
                          <button
                            type="button"
                            onClick={() => handlePower(s, 'reboot')}
                            className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md border border-bd text-t2 hover:bg-s-2"
                          >
                            <RotateCw className="w-3.5 h-3.5" /> Reboot
                          </button>
                        </>
                      )}
                      <button
                        type="button"
                        onClick={() => handleDelete(s)}
                        className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md border border-err-bd text-err hover:bg-err-bg"
                      >
                        <Trash2 className="w-3.5 h-3.5" /> Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {truncated && (
        <p className="text-xs text-warn">
          Note: the account has more servers than one page — list truncated.
        </p>
      )}
      {zoneErrors.length > 0 && (
        <p className="text-xs text-warn">
          Incomplete list — could not read zone{zoneErrors.length > 1 ? 's' : ''}{' '}
          {zoneErrors.map((z) => `${z.zone} (${z.detail ?? z.error})`).join(', ')}.
        </p>
      )}
    </div>
  );
}

function CreateServerForm({ account, onCreated }) {
  const [options, setOptions] = useState(null);
  const [optionsError, setOptionsError] = useState(null);
  const [name, setName] = useState('');
  const [region, setRegion] = useState('');
  const [size, setSize] = useState('');
  const [image, setImage] = useState('');
  const [sshKeyId, setSshKeyId] = useState('');
  const [count, setCount] = useState('1');
  const [allowSmtp, setAllowSmtp] = useState(true);
  const [ipv4Count, setIpv4Count] = useState('1');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const nCount = Math.max(1, Math.min(20, parseInt(count, 10) || 1));
  const nIpv4 = Math.max(1, Math.min(options?.ipv4_max ?? 1, parseInt(ipv4Count, 10) || 1));
  // €/h per IPv4 in the picked zone (Scaleway catalog), null = unknown.
  const ipv4Hourly = region ? (options?.ipv4_price_hourly_by_region?.[region] ?? null) : null;

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const data = await api(`/api/cloud/accounts/${account.id}/create-options`);
        if (alive) setOptions(data);
      } catch (err) {
        if (alive) setOptionsError(err.body?.detail ?? err.message);
      }
    })();
    return () => { alive = false; };
  }, [account.id]);

  // DO + Scaleway sizes carry a regions[] list — only offer sizes valid in the picked region.
  // Scaleway prices differ by zone: price_by_region holds the picked zone's
  // (null there = unknown — never fall back to another zone's price).
  // Cheapest first, unknown prices last.
  const priceOf = (s) => (region && s.price_by_region ? (s.price_by_region[region] ?? null) : s.price_monthly);
  const sizes = (options?.sizes ?? [])
    .filter((s) => !region || !Array.isArray(s.regions) || s.regions.includes(region))
    .map((s) => ({ ...s, shown_price: priceOf(s) }))
    .sort((a, b) => (a.shown_price ?? Infinity) - (b.shown_price ?? Infinity));

  async function handleSubmit(e) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const resp = await api(`/api/cloud/accounts/${account.id}/servers/bulk`, {
        method: 'POST',
        body: {
          name_prefix: name.trim(),
          count: nCount,
          region,
          size,
          image,
          ssh_key_id: sshKeyId === '' ? null : (Number.isNaN(Number(sshKeyId)) ? sshKeyId : Number(sshKeyId)),
          allow_smtp: allowSmtp,
          ...(options.provider === 'scw' ? { ipv4_count: nIpv4 } : {}),
        },
      });
      onCreated(resp);
    } catch (err) {
      setError(err.body?.detail?.join?.(', ') ?? err.body?.detail ?? err.message);
      setSubmitting(false);
    }
  }

  if (optionsError) {
    return (
      <div className="bg-err-bg border border-err-bd text-err text-sm rounded-md px-3 py-2">
        Could not load create options: {optionsError}
      </div>
    );
  }
  if (!options) return <p className="text-sm text-t3">Loading regions, sizes & images…</p>;

  const selectCls = 'mt-1 w-full rounded-md border border-bd-strong px-2.5 py-1.5 text-sm bg-white';

  return (
    <form onSubmit={handleSubmit} className="bg-white border border-bd rounded-md p-4 space-y-3">
      <div className="grid sm:grid-cols-2 lg:grid-cols-6 gap-3">
        <label className="block">
          <span className="text-xs font-medium text-t2">
            {nCount > 1 ? 'Name prefix' : 'Hostname'}
          </span>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={nCount > 1 ? 'batch' : 'my-server-1'}
            className="mt-1 w-full rounded-md border border-bd-strong px-2.5 py-1.5 text-sm font-mono"
          />
          {nCount > 1 && name.trim() && (
            <span className="text-[11px] text-t3">
              → {name.trim()}-1 … {name.trim()}-{nCount}
            </span>
          )}
        </label>
        <label className="block">
          <span className="text-xs font-medium text-t2">Count</span>
          <input
            type="number"
            min="1"
            max="20"
            value={count}
            onChange={(e) => setCount(e.target.value)}
            className="mt-1 w-full rounded-md border border-bd-strong px-2.5 py-1.5 text-sm"
          />
        </label>
        <label className="block">
          <span className="text-xs font-medium text-t2">Region</span>
          <select value={region} onChange={(e) => { setRegion(e.target.value); setSize(''); }} className={selectCls}>
            <option value="">— pick —</option>
            {options.regions.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="text-xs font-medium text-t2">Size / Type</span>
          <select value={size} onChange={(e) => setSize(e.target.value)} className={selectCls}>
            <option value="">— pick —</option>
            {sizes.map((s) => (
              <option key={s.id} value={s.id}>
                {s.label} — {s.shown_price != null ? `${s.currency === 'EUR' ? '€' : '$'}${Number(s.shown_price).toFixed(2)}/mo` : 'price ?'}
              </option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="text-xs font-medium text-t2">Image</span>
          <select value={image} onChange={(e) => setImage(e.target.value)} className={selectCls}>
            <option value="">— pick —</option>
            {options.images.map((i) => <option key={i.id} value={i.id}>{i.label}</option>)}
          </select>
        </label>
        {options.ssh_keys_auto ? (
          <div className="block">
            <span className="text-xs font-medium text-t2">SSH keys</span>
            <p className="mt-1 text-[11px] text-t3">
              {options.ssh_keys === null
                ? 'All SSH keys of the project are installed (this API key cannot list them).'
                : options.ssh_keys.length
                  ? `All ${options.ssh_keys.length} project key(s) are installed: ${options.ssh_keys.map((k) => k.label).join(', ')}.`
                  : 'The project has no SSH keys. Add one in Scaleway IAM first, or nobody can log in.'}
              {' '}No root password is shown for Scaleway.
            </p>
          </div>
        ) : (
          <label className="block">
            <span className="text-xs font-medium text-t2">SSH key (optional)</span>
            <select value={sshKeyId} onChange={(e) => setSshKeyId(e.target.value)} className={selectCls}>
              <option value="">none</option>
              {options.ssh_keys.map((k) => <option key={k.id} value={k.id}>{k.label}</option>)}
            </select>
          </label>
        )}
      </div>

      {options.provider === 'scw' && (
        <div className="flex flex-wrap items-center gap-2 text-sm text-t2">
          <label className="flex items-center gap-2">
            Public IPv4 per server
            <input
              type="number"
              min="1"
              max={options.ipv4_max ?? 5}
              value={ipv4Count}
              onChange={(e) => setIpv4Count(e.target.value)}
              className="w-16 rounded-md border border-bd-strong px-2 py-1 text-sm"
            />
          </label>
          <span className="text-[11px] text-t3">
            {!region
              ? 'pick a region for the IP price'
              : ipv4Hourly == null
                ? 'IPs: price ?'
                : `IPs: ${nIpv4} × €${ipv4Hourly}/h × 730 ≈ €${(nIpv4 * ipv4Hourly * 730).toFixed(2)}/month per server${nCount > 1 ? ` (€${(nCount * nIpv4 * ipv4Hourly * 730).toFixed(2)} for ${nCount})` : ''} — on top of the server price (Scaleway catalog)`}
            {nIpv4 > 1 && ` · all ${nCount * nIpv4} IPs are reserved first: if the zone's IP quota can't hold them, nothing is created`}
          </span>
        </div>
      )}

      {options.provider === 'scw' && (
        <label className="flex items-center gap-2 text-sm text-t2">
          <input type="checkbox" checked={allowSmtp} onChange={(e) => setAllowSmtp(e.target.checked)} />
          Allow outgoing SMTP (25/465/587)
          <span className="text-[11px] text-t3">
            — unblocks the zone's default security group; a server where Scaleway refuses is still created, with a warning
          </span>
        </label>
      )}

      {error && (
        <div className="bg-err-bg border border-err-bd text-err text-sm rounded-md px-3 py-2">{error}</div>
      )}

      <div className="flex items-center gap-2">
        <button
          type="submit"
          disabled={submitting || !name.trim() || !region || !size || !image}
          className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md bg-blue text-white hover:bg-blue-deep disabled:opacity-50"
        >
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
          {nCount > 1 ? `Create ${nCount} servers` : 'Create server'}
        </button>
        <span className="text-xs text-t3">
          Billing starts at the provider as soon as {nCount > 1 ? 'the servers are' : 'the server is'} created.
        </span>
      </div>
    </form>
  );
}
